import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { isStandalone, isIos } from "../lib/pwa";

/**
 * Non-intrusive PWA install banner.
 *
 *   • Android / Desktop Chrome / Edge: listens for the `beforeinstallprompt`
 *     event and shows a one-tap "Installer l'app" button.
 *   • iOS Safari: shows a small helper card with "Share → Add to Home Screen"
 *     instructions (Apple doesn't expose a programmatic prompt).
 *
 * Shows after 12s of session, only on small screens, only if:
 *   - not already installed (standalone display mode)
 *   - user hasn't dismissed it in the last 14 days
 */
const DISMISS_KEY = "nihongo:pwa-install-dismissed-at";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const wasRecentlyDismissed = () => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - Number(v) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

export const InstallPWABanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    const ua = navigator.userAgent || "";
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
    if (!isMobile) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setShow(false);
      setIosTip(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback: no programmatic install — show tip after delay
    let t;
    if (isIos()) {
      t = setTimeout(() => {
        setIosTip(true);
        setShow(true);
      }, 12000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {}
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[60] sm:left-auto sm:right-4 sm:max-w-sm"
      data-testid="pwa-install-banner"
    >
      <div className="bg-[#1A2A40] text-[#FDFBF7] rounded-2xl shadow-brut border-2 border-[#1A2A40] overflow-hidden">
        <div className="p-4 sm:p-5 flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 rounded-xl bg-[#E94A3F] flex items-center justify-center">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-base leading-tight mb-1">
              Installer Japonais Pas à Pas
            </div>
            {iosTip ? (
              <p className="text-xs text-[#FDFBF7]/80 leading-relaxed">
                Touchez{" "}
                <Share className="inline w-3.5 h-3.5 -mt-0.5" /> en bas de Safari, puis{" "}
                <span className="font-bold">
                  « Sur l'écran d'accueil <Plus className="inline w-3 h-3" /> »
                </span>
                .
              </p>
            ) : (
              <p className="text-xs text-[#FDFBF7]/80 leading-relaxed">
                Une icône sur votre écran d'accueil. Plein écran, accès rapide, fonctionne hors-ligne.
              </p>
            )}
          </div>
          <button
            onClick={dismiss}
            aria-label="Fermer"
            data-testid="pwa-install-dismiss"
            className="shrink-0 -mt-1 -mr-1 p-1.5 rounded-lg hover:bg-[#FDFBF7]/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {!iosTip && deferred && (
          <div className="px-4 pb-4 sm:px-5 sm:pb-5">
            <button
              onClick={install}
              data-testid="pwa-install-button"
              className="w-full bg-[#E94A3F] hover:bg-[#d83d33] active:scale-[0.98] text-[#FDFBF7] font-bold py-3 rounded-xl transition-all"
            >
              Installer l'app
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
